import FetchBeneficiaryContacts from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/Queries/FetchBeneficiaryContacts';
import fetchBeneficiaryContacts from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/fetchBeneficiaryContacts';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import CreateContactButton from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryContacts/CreateContactButton';
import ContactCard from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryContacts/ContactCard';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import type BeneficiaryContactView from '@UI/Store/BeneficiaryContacts/Views/BeneficiaryContactView';
import { css } from '@emotion/react';
import { Users } from 'lucide-react';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Skeleton } from '@verclam/ui-kit';

const cardsGridStyle = css`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(min(20rem, 100%), 1fr));
	gap: 1.25rem;
`;

const listStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.875rem;
`;

const noTrustHolderStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-style: italic;
	color: ${theme.colors.inkMuted};
`;

const truncationNoticeStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const SKELETON_CARD_COUNT = 4;
const skeletonCardIndexes = [...Array(SKELETON_CARD_COUNT).keys()];

type Props = {
	beneficiaryId: number;
};

/**
 * Owns the fetch and every loading/error/empty/nominal branch of the contacts screen, once the
 * beneficiary id is known valid.
 *
 * The trust holder, when there is one, always leads the grid — it is the contact who will be able to
 * consent to the record. The screen never paginates (it asks the server's maximum row count directly),
 * but a `totalRecords` past that maximum is surfaced rather than silently dropped.
 *
 * The branch order is deliberate: `error !== null` is checked before the data-absent branch, never the
 * reverse, so an absent `contacts` is never read as a failure (`guides/state-stores.md`, "Ce que
 * `useStoreQuery` ne garantit pas").
 */
const BeneficiaryContactsContent: React.FC<Props> = ({ beneficiaryId }) => {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;

	const {
		data: contacts,
		error,
		refetch,
	} = useStoreQuery({
		selector: (state) => state.beneficiaryContactsStore.contacts,
		stopOn: (value) => value !== null && value.beneficiaryId === beneficiaryId,
		query: () =>
			dispatch(fetchBeneficiaryContacts(new FetchBeneficiaryContacts(facilityId, beneficiaryId))),
		// `facilityId` stays in `deps` even though the current facility is always resolved here:
		// switching agency in the header selector changes it mid-session, and this list must refetch
		// under the new agency rather than keep showing the previous one's contacts.
		deps: [beneficiaryId, facilityId],
	});

	const orderedContacts = React.useMemo((): BeneficiaryContactView[] => {
		if (contacts === null) {
			return [];
		}

		const trustHolder = contacts.list.find((contact) => contact.isPersonOfTrust) ?? null;

		if (trustHolder === null) {
			return [...contacts.list];
		}

		return [trustHolder, ...contacts.list.filter((contact) => contact.id !== trustHolder.id)];
	}, [contacts]);

	const hasTrustHolder = React.useMemo(
		() => orderedContacts.some((contact) => contact.isPersonOfTrust),
		[orderedContacts],
	);

	if (error !== null) {
		return <ErrorState title="Impossible de charger les contacts" onRetry={refetch} />;
	}

	if (contacts === null) {
		return (
			<div css={cardsGridStyle}>
				{skeletonCardIndexes.map((index) => (
					<Skeleton key={index} height="9rem" animation="wave" shape="rectangle" />
				))}
			</div>
		);
	}

	if (contacts.list.length === 0) {
		return (
			<EmptyState
				title="Aucun contact"
				description="L'entourage de la personne accompagnée n'est pas encore renseigné."
				icon={<Users size={28} />}
				action={<CreateContactButton beneficiaryId={beneficiaryId} />}
			/>
		);
	}

	return (
		<div css={listStyle}>
			{!hasTrustHolder && (
				<p css={noTrustHolderStyle}>Aucune personne de confiance n'est désignée pour ce dossier.</p>
			)}
			<div css={cardsGridStyle}>
				{orderedContacts.map((contact) => (
					<ContactCard key={contact.id} contact={contact} />
				))}
			</div>
			{contacts.totalRecords > contacts.list.length && (
				<p css={truncationNoticeStyle}>
					{contacts.totalRecords - contacts.list.length} contact(s) supplémentaire(s) ne sont pas
					affichés.
				</p>
			)}
		</div>
	);
};

export default BeneficiaryContactsContent;
